import { AGE_CLASSES, getLineage, LINEAGES, TERRAINS } from "../data/lineages";
import type {
  AdventureHook,
  AgeClass,
  DragonDossier,
  EncounterPhase,
  Facet,
  FacetRecord,
  Faction,
  GeneratorOptions,
  HistoryEvent,
  HistoryLink,
  HoardItem,
  LairConnection,
  LairRoom,
  RegionalEffect,
  RuleAction
} from "../types";
import { hashString, idFrom, integer, pick, seededRandom, shuffle } from "./rng";
import { repairDossier } from "./validate";

export const GENERATOR_VERSION = "0.1.0";

export const FACETS: Facet[] = [
  "identity",
  "rules",
  "lair",
  "hoard",
  "factions",
  "history",
  "encounter",
  "hooks"
];

export function emptyFacetRecord<T>(value: T): FacetRecord<T> {
  return Object.fromEntries(FACETS.map((facet) => [facet, value])) as FacetRecord<T>;
}

export const DEFAULT_OPTIONS: GeneratorOptions = {
  seed: "ember-crown-9142",
  lineage: "red",
  ageClass: "ancient",
  terrain: "Volcanic",
  tone: "mythic",
  danger: "mythic",
  politics: "high",
  adapter: "srd52",
  locale: "en",
  locks: emptyFacetRecord(false),
  rerolls: emptyFacetRecord(0)
};

const NAME_START = [
  "Vhes",
  "Aurel",
  "Mire",
  "Skad",
  "Kop",
  "Thar",
  "Ilyr",
  "Zor",
  "Caldr",
  "Nyx",
  "Orun",
  "Vael",
  "Sarith",
  "Ember"
];
const NAME_END = ["sar", "ion", "ex", "ara", "yx", "ath", "oth", "ira", "uun", "axis", "or", "ess"];
const TITLES = {
  fire: ["Sulfur Crown", "Ember Sovereign", "Ash-King", "Cinder Tyrant"],
  lightning: ["Storm Diadem", "Glass Thunder", "Crowned Tempest", "Sky's Verdict"],
  poison: ["Thorned Whisper", "Verdant Lie", "Moss-Crowned Oracle", "Briar Regent"],
  acid: ["Drowned Archive", "Caustic Testament", "Blackwater Crown", "Marsh Reclaimer"],
  cold: ["White Silence", "Winter Jaw", "Rime Throne", "Last Blizzard"]
} as const;
const ROLES = [
  "Tyrant",
  "Strategist",
  "Collector",
  "Oracle",
  "Warden",
  "Usurper",
  "Magistrate",
  "Raconteur"
] as const;
const DOMAINS = [
  "tribute-state",
  "forbidden archive",
  "pilgrim road",
  "mining confederacy",
  "storm coast",
  "ruined dynasty",
  "merchant protectorate",
  "hidden cult network"
] as const;
const AGE_INDEX: Record<AgeClass, number> = {
  wyrmling: 0,
  young: 1,
  adult: 2,
  ancient: 3
};

function facetRandom(options: GeneratorOptions, facet: Facet): () => number {
  return seededRandom(
    [
      options.seed.trim().toLowerCase(),
      GENERATOR_VERSION,
      facet,
      options.rerolls[facet],
      options.lineage,
      options.ageClass,
      options.terrain,
      options.tone
    ].join("|")
  );
}

function titleFor(element: string, random: () => number): string {
  const key =
    element.includes("lightning") || element.includes("storm")
      ? "lightning"
      : element.includes("poison")
        ? "poison"
        : element.includes("acid")
          ? "acid"
          : element.includes("frost") || element.includes("cold")
            ? "cold"
            : "fire";
  return pick(random, TITLES[key]);
}

function buildIdentity(options: GeneratorOptions) {
  const random = facetRandom(options, "identity");
  const lineage = getLineage(options.lineage);
  const ageIndex = AGE_INDEX[options.ageClass];
  const start = pick(random, NAME_START);
  const name = `${start}${pick(random, NAME_END)}`.replace(/(.)\1\1/g, "$1$1");
  const title = `the ${titleFor(lineage.element, random)}`;
  const ageRanges: Record<AgeClass, [number, number]> = {
    wyrmling: [3, 5],
    young: [16, 95],
    adult: [101, 790],
    ancient: [801, 1700]
  };
  const temperament = shuffle(
    random,
    [...new Set([
      ...lineage.dispositions,
      "Proud",
      "Patient",
      "Cruel",
      "Curious",
      "Exacting",
      "Restless"
    ])]
  ).slice(0, 4);
  const role = pick(random, ROLES);
  const domainKind = pick(random, DOMAINS);
  const terrain = options.terrain === "Random" ? pick(random, lineage.terrains) : options.terrain;
  const alignment =
    lineage.family === "metallic"
      ? pick(random, ["principled neutral", "sternly benevolent", "honorable"])
      : pick(random, ["imperious", "self-serving", "predatory"]);
  const ageYears = integer(random, ageRanges[options.ageClass][0], ageRanges[options.ageClass][1]);
  return {
    name,
    title,
    lineage: lineage.id,
    lineageLabel: lineage.label,
    ageClass: options.ageClass,
    ageYears,
    role,
    alignment,
    temperament,
    domain: `${terrain} ${domainKind}`,
    summary: `${name} rules a ${terrain.toLowerCase()} domain through ${pick(random, [
      "ritual tribute and carefully cultivated fear",
      "old bargains whose exact wording only the dragon remembers",
      "a network of spies, debtors and prophetic signs",
      "the control of roads, water and buried history"
    ])}.`,
    goal: pick(random, [
      "Turn every local ruler into a willing tributary.",
      "Recover the final relic of a deliberately erased dynasty.",
      "Force an old rival to acknowledge a forgotten treaty.",
      "Shape the region into a monument that will survive the next age."
    ]),
    weakness: pick(random, [
      "Cannot ignore a formal challenge backed by proof.",
      "Mistakes obedience for loyalty.",
      "Protects one seemingly worthless relic above all treasure.",
      "Will preserve the lair's oldest structure even at tactical cost."
    ]),
    threat: options.danger,
    challenge: options.adapter === "neutral" ? null : lineage.challenge[ageIndex]
  };
}

function buildRules(options: GeneratorOptions, identity: ReturnType<typeof buildIdentity>) {
  const random = facetRandom(options, "rules");
  const lineage = getLineage(identity.lineage);
  const ageIndex = AGE_INDEX[identity.ageClass];
  const challenge = lineage.challenge[ageIndex];
  const proficiencyBonus = Math.max(2, Math.ceil(challenge / 4) + 1);
  const armorClass = [17, 18, 19, 22][ageIndex] + (lineage.family === "metallic" ? 0 : integer(random, 0, 1));
  const hpBase = [45, 145, 260, 490][ageIndex];
  const hitPoints = hpBase + integer(random, -10, 20) + challenge * 2;
  const strength = [19, 23, 27, 30][ageIndex];
  const constitution = [17, 21, 25, 29][ageIndex];
  const intelligence = [11, 14, 17, 20][ageIndex] + (lineage.family === "metallic" ? 2 : 0);
  const breathName = `${lineage.element[0].toUpperCase()}${lineage.element.slice(1)} Breath`;
  const actions: RuleAction[] = [
    {
      id: idFrom(`${options.seed}:rules`, "action", 0),
      name: "Multiattack",
      kind: "action",
      description: `The dragon makes a coordinated sequence of bite, claw and movement attacks appropriate to its age.`,
      homebrew: true
    },
    {
      id: idFrom(`${options.seed}:rules`, "action", 1),
      name: breathName,
      kind: "action",
      description: `The dragon exhales ${lineage.damage} energy in a ${lineage.breathShape}. The save DC and damage scale with challenge and adapter.`,
      homebrew: true
    },
    {
      id: idFrom(`${options.seed}:rules`, "action", 2),
      name: "Dreadful Presence",
      kind: "trait",
      description: "Creatures that can see or hear the dragon must master their fear or become shaken by its majesty.",
      homebrew: true
    }
  ];
  if (ageIndex >= 2) {
    actions.push(
      {
        id: idFrom(`${options.seed}:rules`, "action", 3),
        name: "Wing Attack",
        kind: "legendary",
        description: "The dragon beats its wings, disrupts nearby creatures and repositions without yielding control.",
        homebrew: true
      },
      {
        id: idFrom(`${options.seed}:rules`, "action", 4),
        name: "Lair Pulse",
        kind: "lair",
        description: `The lair manifests a ${lineage.element}-themed hazard at a room the dragon can sense.`,
        homebrew: true
      }
    );
  }
  return {
    adapter: options.adapter,
    armorClass,
    hitPoints,
    hitDice: `${[8, 17, 20, 28][ageIndex]}d${[8, 10, 12, 20][ageIndex]} + ${Math.round(hitPoints / 3)}`,
    speed: ["40 ft.", "fly 80 ft.", ...(lineage.id === "black" || lineage.id === "bronze" ? ["swim 40 ft."] : [])],
    abilities: {
      str: strength,
      dex: 10 + integer(random, 0, 4),
      con: constitution,
      int: intelligence,
      wis: 13 + ageIndex * 2,
      cha: 15 + ageIndex * 2
    },
    proficiencyBonus,
    savingThrows: [`Dex +${proficiencyBonus + 2}`, `Con +${proficiencyBonus + 5}`, `Wis +${proficiencyBonus + 3}`],
    skills: [`Perception +${proficiencyBonus + 5}`, `Stealth +${proficiencyBonus + 2}`, `Insight +${proficiencyBonus + 3}`],
    damageImmunities: [lineage.damage],
    senses: [`blindsight ${10 + ageIndex * 10} ft.`, `darkvision ${60 + ageIndex * 30} ft.`, `passive Perception ${14 + proficiencyBonus}`],
    languages: ["Common", "Draconic"],
    actions,
    notice:
      options.adapter === "neutral"
        ? "System-neutral tactical profile. No official challenge rating is asserted."
        : `Original, SRD-compatible ${options.adapter === "srd51" ? "5.1" : "5.2.1"} profile. Review before high-stakes play.`
  };
}

const LAIR_LAYOUTS = [
  "branching cavern",
  "fortified gauntlet",
  "spiral descent",
  "split-level vault",
  "flooded loop"
] as const;

type LairLayout = (typeof LAIR_LAYOUTS)[number];

const ROOM_NOUNS: Record<LairRoom["role"], string[]> = {
  approach: ["Pilgrim Road", "Outer Causeway", "Warning Gallery", "Tribute Ascent"],
  entrance: ["Tribute Gate", "Stone Maw", "Sealed Threshold", "Watcher Arch"],
  chokepoint: ["Tollbridge", "Knife Pass", "Broken Span", "Murder Stair"],
  quarters: ["Oathkeepers' Hall", "Servant Warrens", "Warm Gallery", "Vassal Court"],
  "false-hoard": ["Gilded Decoy", "Painted Treasury", "Coin-Lure Hall", "False Vault"],
  hoard: ["Deep Vault", "Claimed Treasury", "Hoard Core", "Last Counting Room"],
  sanctum: ["Crown Chamber", "Heart Sanctum", "Throne Hollow", "Name-Rite Chamber"],
  escape: ["Sky-Cleft", "Drowned Exit", "Ashen Sluice", "Hidden Eyrie"],
  anomaly: ["Breathing Fault", "Echo Scar", "Impossible Stair", "Omen Well"]
};

function clampPercent(value: number, min = 8, max = 92): number {
  return Math.min(max, Math.max(min, value));
}

const ROLE_TIER: Record<LairRoom["role"], number> = {
  approach: 0,
  entrance: 1,
  chokepoint: 1,
  quarters: 2,
  "false-hoard": 2,
  anomaly: 2,
  hoard: 3,
  sanctum: 3,
  escape: 4
};

const TIER_X = [9, 30, 51, 72, 91];

/**
 * War-table layout: rooms are placed in narrative tiers (approach → threshold →
 * halls → heart → escape) so seals never overlap; each lair layout adds its own
 * flavour on top of the tiered grid.
 */
function tieredLayout(
  roles: LairRoom["role"][],
  layout: LairLayout,
  random: () => number
): { x: number; y: number }[] {
  const byTier = new Map<number, number[]>();
  roles.forEach((role, index) => {
    const tier = ROLE_TIER[role];
    const list = byTier.get(tier) ?? [];
    list.push(index);
    byTier.set(tier, list);
  });
  const points: { x: number; y: number }[] = new Array(roles.length);
  byTier.forEach((indices, tier) => {
    indices.forEach((roomIndex, slot) => {
      const stagger = tier % 2 === 1 ? 8 : 0;
      let y =
        indices.length === 1
          ? 50
          : 16 + stagger + (slot * (68 - stagger * 2)) / (indices.length - 1);
      let dx = integer(random, -2, 2);
      if (layout === "spiral descent") {
        y += (tier - 2) * 5;
      } else if (layout === "fortified gauntlet") {
        y = 50 + (y - 50) * 0.78;
      } else if (layout === "split-level vault") {
        y += y < 50 ? -5 : 5;
      } else if (layout === "flooded loop") {
        dx += Math.round(Math.sin((y / 100) * Math.PI) * 4);
      } else {
        y += y < 50 ? -3 : 3;
      }
      points[roomIndex] = {
        x: clampPercent(TIER_X[tier] + dx),
        y: clampPercent(y + integer(random, -3, 3), 14, 84)
      };
    });
  });
  return points;
}

function roomNameFor(
  role: LairRoom["role"],
  terrain: string,
  lineageElement: string,
  random: () => number
): string {
  const prefix = pick(random, [
    "Ashen",
    "Veiled",
    "Crowned",
    "Broken",
    "Hollow",
    "Stormcut",
    "Gilded",
    "Rootbound",
    terrain,
    lineageElement[0].toUpperCase() + lineageElement.slice(1)
  ]);
  return `${prefix} ${pick(random, ROOM_NOUNS[role])}`;
}

function buildRooms(
  options: GeneratorOptions,
  identity: ReturnType<typeof buildIdentity>,
  layout: LairLayout,
  terrain: string
): LairRoom[] {
  const random = facetRandom(options, "lair");
  const lineage = getLineage(identity.lineage);
  const count = [6, 7, 9, 10][AGE_INDEX[identity.ageClass]];
  const roles: LairRoom["role"][] = [
    "approach",
    "entrance",
    "chokepoint",
    "hoard",
    "sanctum",
    "escape",
    "false-hoard",
    "anomaly",
    "quarters",
    "quarters"
  ];
  const points = tieredLayout(roles.slice(0, count), layout, random);
  return Array.from({ length: count }, (_, index) => {
    const role = roles[index];
    const point = points[index];
    return {
      id: idFrom(`${options.seed}:lair:${options.rerolls.lair}`, "room", index),
      name: roomNameFor(role, terrain, lineage.element, random),
      role,
      danger: index < 2 ? "moderate" : index > count - 4 ? options.danger : "high",
      occupants:
        role === "sanctum"
          ? identity.name
          : role === "hoard"
            ? "Silent treasurers, trapped ledgers and one visible guard"
            : role === "false-hoard"
              ? "Decoy guards trained to retreat loudly"
          : pick(random, [
              "Tribute guards and a watchful emissary",
              "Bound elemental servants",
              "A small but disciplined cult cell",
              "No permanent occupants",
              "Map-scribes marking old damage in fresh ink"
            ]),
      hazards: shuffle(random, [
        `${lineage.element} vents`,
        "unstable stone",
        "concealed alarm sigils",
        "collapsing ledges",
        "blinding mineral haze",
        layout === "flooded loop" ? "rising black water" : "echoing murder holes",
        layout === "spiral descent" ? "false stairs over a shaft" : "tilted floor plates"
      ]).slice(0, integer(random, 1, 3)),
      treasure:
        role === "hoard"
          ? "The hidden core of the hoard"
          : role === "false-hoard"
            ? "Visible decoy coinage"
            : pick(random, ["Minor tribute caches", "Ceremonial objects", "No obvious treasure"]),
      tacticalNote: pick(random, [
        "The dragon can split intruders into separate routes here.",
        "Vertical access lets the dragon disengage without surrendering the room.",
        "Noise carries to two adjacent chambers.",
        "A negotiator can invoke the room's original purpose to delay violence.",
        layout === "branching cavern"
          ? "Side passages let defenders appear behind intruders after one round."
          : "The room changes value once a secret route is discovered."
      ]),
      x: Math.round(point.x),
      y: Math.round(point.y),
      radius: integer(random, 5, 9)
    };
  });
}

function buildLair(options: GeneratorOptions, identity: ReturnType<typeof buildIdentity>) {
  const random = facetRandom(options, "lair");
  const terrain = options.terrain === "Random" ? pick(random, getLineage(identity.lineage).terrains) : options.terrain;
  const layout = pick(random, LAIR_LAYOUTS);
  const rooms = buildRooms(options, identity, layout, terrain);
  const connections: LairConnection[] = [];
  const seen = new Set<string>();
  const addConnection = (fromIndex: number, toIndex: number, secret = false, escape = false) => {
    const from = rooms[fromIndex];
    const to = rooms[toIndex];
    if (!from || !to || from.id === to.id) return;
    const key = [from.id, to.id].sort().join(":");
    if (seen.has(key)) return;
    seen.add(key);
    connections.push({
      id: idFrom(`${options.seed}:lair:${options.rerolls.lair}`, "path", connections.length),
      from: from.id,
      to: to.id,
      secret,
      escape: escape || to.role === "escape"
    });
  };
  rooms.slice(1).forEach((room, index) => {
    addConnection(index, index + 1, index === 3 || (index > 4 && random() > 0.74), room.role === "escape");
  });
  const hoardIndex = rooms.findIndex((room) => room.role === "hoard");
  const sanctumIndex = rooms.findIndex((room) => room.role === "sanctum");
  const falseHoardIndex = rooms.findIndex((room) => room.role === "false-hoard");
  const anomalyIndex = rooms.findIndex((room) => room.role === "anomaly");
  const escapeIndex = rooms.findIndex((room) => room.role === "escape");
  addConnection(2, hoardIndex, layout === "fortified gauntlet");
  addConnection(falseHoardIndex, hoardIndex, true);
  addConnection(anomalyIndex, sanctumIndex, true);
  addConnection(sanctumIndex, escapeIndex, true, true);
  if (layout === "flooded loop" || layout === "spiral descent") {
    addConnection(rooms.length - 1, 1, layout === "spiral descent", rooms[rooms.length - 1]?.role === "escape");
  }
  if (layout === "branching cavern" && rooms.length > 8) {
    addConnection(1, rooms.length - 2, true);
  }
  const secretRoutes = connections.filter((connection) => connection.secret).length;
  const treasureRooms = rooms.filter((room) => room.role === "hoard" || room.role === "false-hoard").length;
  return {
    name: pick(random, [
      "Kraterfort",
      "The Vault of Seven Echoes",
      "Cinder Crown Citadel",
      "The Drowned Archive",
      "Stormglass Eyrie",
      "The Below-Crown Labyrinth",
      "Oathscar Warrens"
    ]),
    type: `${terrain} ${layout}`,
    terrain,
    layout,
    mapNotes: [
      `${layout} with ${secretRoutes} secret route${secretRoutes === 1 ? "" : "s"}`,
      `${treasureRooms} treasure-facing chamber${treasureRooms === 1 ? "" : "s"}`,
      `${escapeIndex >= 0 ? "Dedicated escape route" : "No clean escape route"} near the inner complex`
    ],
    summary: `A ${layout} in ${terrain.toLowerCase()} terrain, built around layered access, false value, secret movement and a protected inner hoard.`,
    rooms,
    connections
  };
}

function buildHoard(options: GeneratorOptions, identity: ReturnType<typeof buildIdentity>, factions: Faction[]) {
  const random = facetRandom(options, "hoard");
  const ageMultiplier = [1, 4, 12, 28][AGE_INDEX[identity.ageClass]];
  const totalValue = Math.round((2500 + integer(random, 0, 1800)) * ageMultiplier / 100) * 100;
  const visible = integer(random, 20, 38);
  const claimed = integer(random, 12, 28);
  const cursed = integer(random, 5, 14);
  const hidden = 100 - visible - claimed - cursed;
  const itemCount = [1, 2, 3, 4][AGE_INDEX[identity.ageClass]];
  const itemNames = shuffle(random, [
    "The Crown of the Last Ash-King",
    "Admiral Senra's Unbroken Seal",
    "The Bell That Rang Beneath the Sea",
    "Mirror of the Nameless Heir",
    "Seven Links of the Giant's Oath",
    "The Verdigris Testament",
    "The Debt-Key of Orun",
    "The Sapphire Map of Vanished Roads"
  ]);
  const claimants = shuffle(random, [
    ...factions.map((faction) => faction.name),
    "Three rival heirs",
    "A vanished temple order",
    "The last surviving witness",
    "A city that no longer exists"
  ]);
  const items: HoardItem[] = Array.from({ length: itemCount }, (_, index) => ({
    id: idFrom(`${options.seed}:hoard:${options.rerolls.hoard}`, "item", index),
    name: itemNames[index % itemNames.length],
    rarity: pick(random, ["Signature relic", "Legendary provenance", "Rare diplomatic treasure"]),
    value: Math.round((totalValue * (0.06 + random() * 0.11)) / 100) * 100,
    claimedBy: claimants[index % claimants.length],
    origin: pick(random, [
      "Taken during the fall of Caldrin Keep",
      "Paid as tribute after a war no chronicle admits occurred",
      "Recovered from a drowned coronation vault",
      "Won through the exact wording of an ancient wager",
      `Seized after ${identity.name} proved a treaty had one missing witness`
    ]),
    complication: pick(random, [
      "Removing it awakens an old treaty-curse.",
      "Its legal owner is still alive under another name.",
      "It is the key to a sealed room elsewhere in the lair.",
      "Three convincing counterfeits are displayed nearby."
    ]),
    hook: pick(random, [
      "The relic proves the reigning monarch is illegitimate.",
      "A faction will exchange safe passage for its destruction.",
      "Returning it would end a regional curse and start a civil war.",
      "The dragon wants someone else to steal it publicly."
    ])
  }));
  return {
    totalValue,
    distribution: { visible, hidden, claimed, cursed },
    items
  };
}

function buildFactions(options: GeneratorOptions, identity: ReturnType<typeof buildIdentity>): Faction[] {
  const random = facetRandom(options, "factions");
  const count = [3, 4, 6, 7][AGE_INDEX[identity.ageClass]];
  const relations: Faction["relation"][] = ["worships", "fears", "bargains", "spies", "rivals", "serves"];
  const palette = ["#d86532", "#6f8d4a", "#748fc2", "#9566a5", "#c69a4b", "#9a4a42", "#4e9792"];
  const names = shuffle(random, [
    "Cult of the Crown",
    "House Velkar",
    "The Ember Network",
    "Molten Scions",
    "The Athen Tributaries",
    "Kaelen the Oath-Breaker",
    "Mirex of the Blue Reed",
    "The Silent Cartographers",
    "The Bellward Priory",
    "Guild of Open Locks",
    "The Salt-Black Admiralty",
    "The Last Tax Court"
  ]);
  return Array.from({ length: count }, (_, index) => {
    const angle = (index / count) * Math.PI * 2 - Math.PI / 2;
    return {
      id: idFrom(`${options.seed}:factions:${options.rerolls.factions}`, "faction", index),
      name: names[index % names.length],
      kind: pick(random, ["cult", "noble house", "spy network", "retinue", "tributary city", "rival dragon"]),
      relation: relations[index % relations.length],
      power: integer(random, 2, 9),
      goal: pick(random, [
        `Win ${identity.name}'s recognition without paying the requested price.`,
        "Control access to the hoard's disputed relics.",
        "Replace the current tribute system with a private monopoly.",
        "Expose a betrayal before the dragon discovers it independently."
      ]),
      color: palette[index % palette.length],
      x: Math.round(50 + Math.cos(angle) * 36),
      y: Math.round(50 + Math.sin(angle) * 36)
    };
  });
}

function buildHistory(
  options: GeneratorOptions,
  identity: ReturnType<typeof buildIdentity>,
  lair: ReturnType<typeof buildLair>,
  hoard: ReturnType<typeof buildHoard>,
  factions: Faction[]
): HistoryEvent[] {
  const random = facetRandom(options, "history");
  const count = [6, 7, 9, 11][AGE_INDEX[identity.ageClass]];
  const roomByRole = (role: LairRoom["role"]) => lair.rooms.find((room) => room.role === role) ?? lair.rooms[0];
  const firstItem = hoard.items[0];
  const secondItem = hoard.items[1] ?? firstItem;
  const claimant =
    factions.find((faction) => faction.name === firstItem?.claimedBy) ??
    factions.find((faction) => faction.relation === "bargains") ??
    factions[0];
  const rival = factions.find((faction) => faction.relation === "rivals") ?? factions.at(-1) ?? claimant;
  const spy = factions.find((faction) => faction.relation === "spies") ?? claimant;
  const servant = factions.find((faction) => faction.relation === "serves") ?? claimant;
  const link = (type: HistoryLink["type"], id: string, name: string, reason: string): HistoryLink => ({
    id,
    type,
    name,
    reason
  });
  const templates = [
    {
      ageRatio: 0.02,
      era: "Origin",
      title: `Hatched under ${roomByRole("approach").name}`,
      description: `${identity.name}'s first known sign was a ${getLineage(identity.lineage).element} omen that made the outer approach impossible to map for one night.`,
      consequence: `The approach still reacts to the dragon's name and misleads anyone carrying stolen tribute.`,
      witness: roomByRole("approach").name,
      links: [
        link("dragon", identity.name, identity.name, "birth omen"),
        link("room", roomByRole("approach").id, roomByRole("approach").name, "first recorded site")
      ]
    },
    {
      ageRatio: 0.12,
      era: "First Claim",
      title: `The first debt entered ${roomByRole("hoard").name}`,
      description: firstItem
        ? `${firstItem.name} entered the hoard after ${identity.name} turned a private bargain into public law.`
        : `${identity.name} made the hoard a legal institution before it became a pile of treasure.`,
      consequence: firstItem?.complication ?? "Every treasure now has a claimant, even when the claimant is dead.",
      witness: claimant?.name ?? roomByRole("hoard").name,
      links: [
        link("room", roomByRole("hoard").id, roomByRole("hoard").name, "treasure seat"),
        ...(firstItem ? [link("item", firstItem.id, firstItem.name, "first disputed relic")] : [])
      ]
    },
    {
      ageRatio: 0.22,
      era: "Bargain",
      title: `${claimant?.name ?? "A mortal house"} bought a promise it could not afford`,
      description: `${claimant?.name ?? "A mortal house"} received protection and lost the right to tell its own version of the bargain.`,
      consequence: claimant?.goal ?? "The bargain survives as a regional obligation.",
      witness: claimant?.name ?? "Unknown claimant",
      links: claimant ? [link("faction", claimant.id, claimant.name, "original claimant")] : []
    },
    {
      ageRatio: 0.34,
      era: "Construction",
      title: `${lair.name} became a ${lair.layout ?? "layered fortress"}`,
      description: `The lair was rebuilt so that ${roomByRole("false-hoard").name} and ${roomByRole("sanctum").name} tell different stories about the same treasure.`,
      consequence: lair.mapNotes?.[0] ?? "Secret movement became part of the lair's politics.",
      witness: roomByRole("false-hoard").name,
      links: [
        link("lair", lair.name, lair.name, "rebuilt stronghold"),
        link("room", roomByRole("false-hoard").id, roomByRole("false-hoard").name, "deception chamber"),
        link("room", roomByRole("sanctum").id, roomByRole("sanctum").name, "true seat")
      ]
    },
    {
      ageRatio: 0.48,
      era: "Schism",
      title: `${spy?.name ?? "Spies"} found the wrong route`,
      description: `${spy?.name ?? "Spies"} discovered a secret connection, but the map they sold leads to ${roomByRole("anomaly").name}.`,
      consequence: roomByRole("anomaly").tacticalNote,
      witness: spy?.name ?? roomByRole("anomaly").name,
      links: [
        ...(spy ? [link("faction", spy.id, spy.name, "map thief")] : []),
        link("room", roomByRole("anomaly").id, roomByRole("anomaly").name, "false route")
      ]
    },
    {
      ageRatio: 0.58,
      era: "War",
      title: `${rival?.name ?? "A rival"} failed to take ${secondItem?.name ?? "the hoard"}`,
      description: `${identity.name} let the rival survive so every faction would remember that losing to the dragon can be more useful than dying.`,
      consequence: secondItem?.hook ?? "The defeated rival still knows one path through the lair.",
      witness: rival?.name ?? "Defeated rival",
      links: [
        ...(rival ? [link("faction", rival.id, rival.name, "defeated rival")] : []),
        ...(secondItem ? [link("item", secondItem.id, secondItem.name, "war prize")] : [])
      ]
    },
    {
      ageRatio: 0.7,
      era: "Rule",
      title: `${servant?.name ?? "A retinue"} made tribute routine`,
      description: `${servant?.name ?? "A retinue"} turned fear into schedules, receipts and escorted pilgrim routes.`,
      consequence: `The domain now behaves like a state even where no mortal crown admits ${identity.name}'s authority.`,
      witness: servant?.name ?? "Tribute clerks",
      links: servant ? [link("faction", servant.id, servant.name, "tribute machinery")] : []
    },
    {
      ageRatio: 0.82,
      era: "Omen",
      title: `${roomByRole("sanctum").name} answered the region`,
      description: `The lair's inner chamber began changing roads, dreams and weather whenever the hoard was threatened.`,
      consequence: `Regional effects now point toward ${lair.name} even when maps are burned.`,
      witness: roomByRole("sanctum").name,
      links: [
        link("room", roomByRole("sanctum").id, roomByRole("sanctum").name, "lair heart"),
        link("region", "regional-effects", "Regional effects", "current omen pattern")
      ]
    },
    {
      ageRatio: 0.92,
      era: "Pressure",
      title: `${firstItem?.claimedBy ?? "A claimant"} renews the old claim`,
      description: firstItem
        ? `${firstItem.claimedBy} can now prove a claim to ${firstItem.name}, but doing so exposes why it was hidden.`
        : `A claimant can now prove the dragon's oldest bargain was incomplete.`,
      consequence: firstItem?.hook ?? "The claim can end one war by starting another.",
      witness: firstItem?.claimedBy ?? "Unknown claimant",
      links: [
        ...(firstItem ? [link("item", firstItem.id, firstItem.name, "renewed claim")] : []),
        ...(claimant ? [link("faction", claimant.id, claimant.name, "living claimant")] : [])
      ]
    },
    {
      ageRatio: 1,
      era: "Now",
      title: "The present conflict begins",
      description: `The current hook is not random loot: it is the moment when ${lair.name}, ${firstItem?.name ?? "the hoard"} and ${rival?.name ?? "a rival faction"} all become visible at once.`,
      consequence: `Any party intervention changes ownership, access and the region's official memory.`,
      witness: identity.name,
      links: [
        link("dragon", identity.name, identity.name, "central actor"),
        link("lair", lair.name, lair.name, "conflict stage"),
        ...(firstItem ? [link("item", firstItem.id, firstItem.name, "central prize")] : []),
        ...(rival ? [link("faction", rival.id, rival.name, "active pressure")] : [])
      ]
    }
  ];
  const selectedTemplates =
    count >= templates.length
      ? templates
      : [...templates.slice(0, Math.max(0, count - 1)), templates[templates.length - 1]];

  return selectedTemplates.map((template, index) => {
    const age = Math.max(
      1,
      Math.min(identity.ageYears, Math.round(identity.ageYears * template.ageRatio + integer(random, -4, 8)))
    );
    return {
      id: idFrom(`${options.seed}:history:${options.rerolls.history}`, "event", index),
      age,
      era: template.era,
      title: template.title,
      description: template.description,
      consequence: template.consequence,
      witness: template.witness,
      linkedEntity: template.links[0]?.name ?? identity.name,
      linkedEntities: template.links
    };
  });
}

function buildRegionalEffects(options: GeneratorOptions, identity: ReturnType<typeof buildIdentity>): RegionalEffect[] {
  const random = facetRandom(options, "lair");
  const lineage = getLineage(identity.lineage);
  return [
    {
      id: idFrom(options.seed, "regional", 0),
      name: `${lineage.element[0].toUpperCase()}${lineage.element.slice(1)} Weather`,
      description: `Unnatural ${lineage.element} signs gather over roads that lead toward the lair.`
    },
    {
      id: idFrom(options.seed, "regional", 1),
      name: "Tribute Dreams",
      description: pick(random, [
        "People sleeping nearby dream of objects they owe but never possessed.",
        "Old promises appear as gold writing on dark water.",
        "Predators leave orderly piles of bones at boundary stones."
      ])
    },
    {
      id: idFrom(options.seed, "regional", 2),
      name: "Distorted Distance",
      description: "Routes toward the sanctum feel shorter to invited guests and longer to thieves."
    }
  ];
}

function buildEncounter(options: GeneratorOptions, identity: ReturnType<typeof buildIdentity>): EncounterPhase[] {
  const random = facetRandom(options, "encounter");
  const phases: EncounterPhase[] = [
    {
      id: idFrom(`${options.seed}:encounter:${options.rerolls.encounter}`, "phase", 0),
      kind: "social",
      title: "Social opening",
      steps: [
        `${identity.name} demands proper titles and a concrete tribute.`,
        "The dragon prefers negotiation over immediate violence.",
        "Respect reveals strength; flattery without evidence is treated as weakness."
      ]
    },
    {
      id: idFrom(`${options.seed}:encounter:${options.rerolls.encounter}`, "phase", 1),
      kind: "stealth",
      title: "Stealth opening",
      steps: [
        "Faction spies notice disturbed seals before the dragon does.",
        "The false-hoard chamber becomes active.",
        "The dragon waits above the most valuable exit route."
      ]
    },
    {
      id: idFrom(`${options.seed}:encounter:${options.rerolls.encounter}`, "phase", 2),
      kind: "combat",
      title: "Combat phases",
      steps: [
        pick(random, ["Wing buffet and breath control.", "Breath weapon across the central causeway."]),
        "Summon or command lair servants.",
        "Use the map to divide the party before committing to melee."
      ]
    },
    {
      id: idFrom(`${options.seed}:encounter:${options.rerolls.encounter}`, "phase", 3),
      kind: "parley",
      title: "Parley condition",
      steps: ["Prove worth or bring a tribute the dragon desires.", "Never lie about intentions after parley begins."]
    },
    {
      id: idFrom(`${options.seed}:encounter:${options.rerolls.encounter}`, "phase", 4),
      kind: "escape",
      title: "Escape condition",
      steps: ["The dragon escapes if the sanctum becomes structurally unsafe.", "Destroying key supports may collapse the lair."]
    },
    {
      id: idFrom(`${options.seed}:encounter:${options.rerolls.encounter}`, "phase", 5),
      kind: "treasure",
      title: "Treasure consequence",
      steps: ["Taking the signature relic triggers a regional response.", "Stealing disputed items creates living claimants, not anonymous loot."]
    }
  ];
  return phases;
}

function buildHooks(
  options: GeneratorOptions,
  identity: ReturnType<typeof buildIdentity>,
  factions: Faction[],
  items: HoardItem[]
): AdventureHook[] {
  const random = facetRandom(options, "hooks");
  return Array.from({ length: 4 }, (_, index) => ({
    id: idFrom(`${options.seed}:hooks:${options.rerolls.hooks}`, "hook", index),
    title: pick(random, [
      "The Impossible Tribute",
      "A Crown with Three Owners",
      "The Rival's Invitation",
      "The Empty Room",
      "Ashes of the Tax Ledger",
      "The Dragon's Witness"
    ]),
    premise: pick(random, [
      `${identity.name} offers peace if the party retrieves an object no one admits exists.`,
      "A claimant hires the party to steal evidence rather than treasure.",
      "One lair chamber has vanished from every map but still influences the others.",
      "A rival asks the party to arrange a public humiliation without causing a war."
    ]),
    stakes: pick(random, [
      "Failure transfers a city-sized debt to innocent heirs.",
      "Success ends one conflict and legitimizes a worse ruler.",
      "The region has seven days before its roads become impassable.",
      "Every faction will know who touched the relic."
    ]),
    linkedEntity: items[index % Math.max(1, items.length)]?.name ?? factions[index % factions.length]?.name ?? identity.name
  }));
}

function deterministicDate(seed: string): string {
  const year = 2024 + (hashString(seed) % 3);
  const month = hashString(`${seed}:month`) % 12;
  const day = (hashString(`${seed}:day`) % 27) + 1;
  return new Date(Date.UTC(year, month, day, 12, 0, 0)).toISOString();
}

function buildAppearance(options: GeneratorOptions, identity: ReturnType<typeof buildIdentity>) {
  const random = facetRandom(options, "identity");
  const lineage = getLineage(identity.lineage);
  const horns = pick(random, [
    "asymmetrical crown-horns",
    "back-swept obsidian antlers",
    "broken temple-spire horns",
    "low plated brow-horns",
    "twin hooked dueling horns"
  ]);
  const wings = pick(random, [
    "one wing veined like cracked glass",
    "wide storm-sail wings",
    "ragged war-banner wings",
    "narrow falconing wings",
    "membranous wings marked with old tribute glyphs"
  ]);
  const eyes = pick(random, [
    "banked-coal eyes",
    "coin-bright slit pupils",
    "opal eyes with no visible pupil",
    "storm-lit silver eyes",
    "green-gold judicial eyes"
  ]);
  const signatureFeature = pick(random, [
    "a missing scale over the heart that never heals",
    "a chain of fused coins grown into the neck plates",
    "a crown-shaped burn scar across the muzzle",
    "a tail barb wrapped in oath-rings",
    "a breath glow visible through the ribs before anger"
  ]);
  return {
    element: lineage.element,
    accent: lineage.accent,
    accentSoft: lineage.accentSoft,
    silhouette: pick(random, ["crowned", "serpentine", "broad-winged", "thorn-crested", "hook-jawed"]),
    markings: pick(random, ["ember scars", "ritual gold", "weathered plates", "pale fracture lines", "map-like scale scars"]),
    horns,
    wings,
    eyes,
    signatureFeature,
    battleScars: pick(random, [
      "old ballista scars along the left shoulder",
      "a healed bite mark from a rival dragon",
      "acid-pitted armor plates around the throat",
      "ritual brands hidden under newer scales",
      "one claw replaced by a polished relic talon"
    ]),
    voice: pick(random, [
      "a furnace behind a bronze door",
      "thunder heard through stone",
      "ice cracking beneath deep water",
      "a judge reading a sentence in a sealed room"
    ]),
    scent: pick(random, ["sulfur and hot iron", "wet stone and old coins", "ozone, cedar and dust", "rain on ancient ash"]),
    portrait: `assets/dragons/${lineage.id}.webp`
  };
}

export function generateDossier(options: GeneratorOptions): DragonDossier {
  const normalized: GeneratorOptions = {
    ...options,
    seed: options.seed.trim() || DEFAULT_OPTIONS.seed,
    lineage: LINEAGES.some((lineage) => lineage.id === options.lineage) ? options.lineage : "red",
    ageClass: AGE_CLASSES.includes(options.ageClass) ? options.ageClass : "ancient",
    terrain: options.terrain || pick(seededRandom(options.seed), TERRAINS)
  };
  const identity = buildIdentity(normalized);
  const rules = buildRules(normalized, identity);
  const lair = buildLair(normalized, identity);
  const factions = buildFactions(normalized, identity);
  const hoard = buildHoard(normalized, identity, factions);
  const history = buildHistory(normalized, identity, lair, hoard, factions);
  const regionalEffects = buildRegionalEffects(normalized, identity);
  const encounterScript = buildEncounter(normalized, identity);
  const hooks = buildHooks(normalized, identity, factions, hoard.items);
  const lineage = getLineage(identity.lineage);
  const appearance = buildAppearance(normalized, identity);
  const relationships = factions.map((faction, index) => ({
    id: idFrom(`${normalized.seed}:relationships`, "relation", index),
    source: faction.id,
    target: "dragon",
    type: faction.relation,
    label: faction.relation
  }));

  return repairDossier({
    manifest: {
      seed: normalized.seed,
      generatorVersion: GENERATOR_VERSION,
      rulesAdapter: normalized.adapter,
      locale: normalized.locale,
      generatedAt: deterministicDate(normalized.seed),
      locks: { ...normalized.locks },
      rerolls: { ...normalized.rerolls }
    },
    dragon: identity,
    appearance,
    rules,
    lair,
    hoard,
    factions,
    relationships,
    history,
    regionalEffects,
    encounterScript,
    hooks,
    assets: {
      portrait: `assets/dragons/${lineage.id}.webp`,
      portraitAlt: `${identity.name}, ${appearance.signatureFeature}, an original ${identity.lineageLabel.toLowerCase()} illustration`,
      mapStyle: "obsidian"
    },
    revision: 0
  });
}
